import React, {
    useMemo, 
    useEffect, 
    useRef, 
    useState, 
    useCallback,
} from "react";
import {
    Autocomplete, 
    TextField, 
    Checkbox, 
    Box, 
    CircularProgress,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { userServicer } from "../../Commen_Services/user.services";
import { InputType } from "../../../_enums/enum.services";

interface Option {
    OptionID: string | number;
    OptionName: string;
}

interface MultiSelectProps {
    name: string;
    headerName: string;
    label: string;
    autoSearchKey: string;
    onChange: (name: string, headerName: string, selected: Option[]) => void;
    selectedValues: string;
    fieldType: any;
    autoFocus?: boolean;
}

const filterOptions = createFilterOptions<Option>({ matchFrom: "any", limit: 500 });

const MultiSelect: React.FC<MultiSelectProps> = ({
    name,
    headerName,
    label,
    autoSearchKey,
    onChange,
    selectedValues,
    fieldType,
    autoFocus = true,
}) => {
    const [tempAllOptions, setTempAllOptions] = useState<Option[]>([]);
    const [options, setOptions] = useState<Option[]>([]);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [testSelectedOption, setTestSelectedOption] = useState<string>("");
    const [allSelected, setAllSelected] = useState(false);
    const isFirstRender = useRef(true);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const limit = 10;
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const allOptions = useMemo(() => (
        fieldType === InputType.MultiSelect
            ? [{ OptionID: -1, OptionName: "Select All" }, ...options]
            : options
    ), [options, fieldType]);

    const fetchData = useCallback(async (searchKey: string) => {
        setLoading(true);
        try {
            const { data } = await userServicer.getDropDownlist(searchKey, null);
            const transformed = data.map((item: any) => ({
                OptionID: item[Object.keys(item)[0]],
                OptionName: String(item[Object.keys(item)[1]] ?? item[Object.keys(item)[0]]),
            }));
            //setOptions(transformed);
            setTempAllOptions(transformed);
            setOptions(transformed.slice(0, limit));
            setPage(1);
            setHasMore(transformed.length > limit);
        } catch (err) {
            console.error("Dropdown fetch error:", err);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(autoSearchKey);
    }, [autoSearchKey, fetchData]);

    useEffect(() => {
        if (selectedValues !== testSelectedOption) {
            const normalizedSelectedValues = Array.isArray(selectedValues)
                ? selectedValues.map(String).join(",")
                : selectedValues == null
                    ? ""
                    : String(selectedValues);
            setTestSelectedOption(normalizedSelectedValues);
        }
    }, [selectedValues]);

    useEffect(() => {
    if (autoFocus && isFirstRender.current && inputRef.current) {
        inputRef.current.focus();
        isFirstRender.current = false;
    }
    }, [autoFocus]);

    useEffect(() => {
        const debounceTimer = window.setTimeout(() => {
            if (!inputValue.trim()) {
                setOptions(tempAllOptions.slice(0, limit));
                setPage(1);
                setHasMore(tempAllOptions.length > limit);
                return;
            }

            const filtered = tempAllOptions.filter((o) =>
                String(o.OptionName).toLowerCase().includes(inputValue.toLowerCase())
            );
            setOptions(filtered);
            setHasMore(false);
        }, 300);

        return () => window.clearTimeout(debounceTimer);
    }, [inputValue, tempAllOptions]);

    const handleInputChange = (_: any, value: string) => {
        setInputValue(value);
    };


    const handleChange = (
        event: React.SyntheticEvent,
        newValues: Option[] | Option
    ) => {
        // debugger
        event.preventDefault();
        event.stopPropagation();

        const normalized: Option[] = Array.isArray(newValues) ? newValues : [newValues];
        let updated = [...normalized];

        if (fieldType === InputType.MultiSelect) {
            if (normalized.some(opt => opt.OptionID === -1)) {
                // setLoading(true);
                updated = allSelected ? [] : [...tempAllOptions];
                setAllSelected(!allSelected);
            } else {
                setAllSelected(updated.length === tempAllOptions.length);
            }
        }

        const idsCSV = updated
            .map(opt => String(opt.OptionID).trim())
            .join(",");

        setTestSelectedOption(idsCSV);
        onChange(name, headerName, updated);
    };

    const handleSingleChange = (
    _: React.SyntheticEvent,
    value: Option | null
) => {
    const updated = value ? [value] : [];

    const idsCSV = updated
        .map(opt => String(opt.OptionID).trim())
        .join(",");

    setTestSelectedOption(idsCSV);
    onChange(name, headerName, updated);
};

const handleScroll = (e: React.SyntheticEvent) => {
    const node = e.currentTarget;
    if (
      node.scrollTop + node.clientHeight >= node.scrollHeight - 10 &&
      hasMore &&
      !loading
    ) {
      const next = tempAllOptions.slice(page * limit, (page + 1) * limit);
      setOptions((prev) => [...prev, ...next]);
      setPage((p) => p + 1);
      if (next.length < limit) setHasMore(false);
    }
  };

    const selectedOptions = useMemo(() => {
        // debugger
        if (!testSelectedOption || tempAllOptions.length === 0) {
            return [];
        }

        const ids = String(testSelectedOption)
            .split(",")
            .map(id => id)
            .filter(Boolean)
            .map(id => (isNaN(Number(id)) ? id : Number(id)));

        const normalized = tempAllOptions.filter(
            (opt) => ids.includes(opt.OptionID) || ids.includes(opt.OptionName)
        );
        if (fieldType === InputType.MultiSelect) {
            if (normalized.some(opt => opt.OptionID === -1)) {
                setAllSelected(!allSelected);
            } else {
                setAllSelected(normalized.length === options.length);
            }
        }
        return normalized;
    }, [testSelectedOption, tempAllOptions, fieldType]);

    return (
        <>
            {fieldType === InputType.MultiSelect ? (
                <div className={`report-filter ${open ? "dropdown" : ""} `}>
                <Autocomplete<Option, true, false, false>
                    multiple
                    options={allOptions}
                    limitTags={1}
                    // filterOptions={filterOptions}
                    open={open}
                    loading={loading}
                    onClose={() => setOpen(false)}
                    disablePortal
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.OptionName}
                    value={selectedOptions}
                    isOptionEqualToValue={(opt, val) => opt.OptionID === val.OptionID}
                    getLimitTagsText={() =>
                        allSelected
                            ? `+${tempAllOptions.length - 1}`
                            : `+${selectedOptions.length - 1}`
                    }
                    ListboxProps={{ onScroll: handleScroll }}
                    onChange={handleChange}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    renderOption={(props, option, { selected }) => (
                        <Box component="li" {...props} key={option.OptionID}>
                            <Checkbox checked={allSelected ? allSelected : selected} />
                            {option.OptionName}
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={label}
                            variant="standard"
                            inputRef={inputRef}
                            onClick={() => setOpen(true)}
                            InputLabelProps={{
                                style: { color: "var(--body-main-dark-color)", fontSize: "12px" },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading && (
                                            <CircularProgress color="inherit" size={16} />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                </div>
            ) : (
                <Autocomplete<Option, false, false, false>
                    disablePortal
                    options={allOptions}
                    value={selectedOptions[0] || null}
                    onChange={handleSingleChange}
                    getOptionLabel={(option) => option.OptionName}
                    isOptionEqualToValue={(opt, val) => opt.OptionID === val.OptionID}
                    renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.OptionID}>
                            {option.OptionName}
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={label}
                            variant="standard"
                            inputRef={inputRef}
                            InputLabelProps={{
                                style: {
                                    color: "var(--body-main-dark-color)",
                                    fontSize: "12px",
                                },
                            }}
                        />
                    )}
                />
            )}
        </>
    );
};

export default MultiSelect;



// import React, {
//     useMemo,
//     useEffect,
//     useRef,
//     useState,
//     useCallback,
// } from "react";
// import {
//     Autocomplete,
//     TextField,
//     Checkbox,
//     Box,
//     CircularProgress,
// } from "@mui/material";
// import { createFilterOptions } from "@mui/material/Autocomplete";
// import { userServicer } from "../../Commen_Services/user.services";
// import { InputType } from "../../../_enums/enum.services";

// interface Option {
//     OptionID: string | number;
//     OptionName: string;
// }

// interface MultiSelectProps {
//     name: string;
//     headerName: string;
//     label: string;
//     autoSearchKey: string;
//     onChange: (name: string, headerName: string, selected: Option[]) => void;
//     selectedValues: string;
//     fieldType: any;
// }

// const limit = 100;

// const MultiSelect2: React.FC<MultiSelectProps> = ({
//     name,
//     headerName,
//     label,
//     autoSearchKey,
//     onChange,
//     selectedValues,
//     fieldType,
// }) => {
//     const [tempAllOptions, setTempAllOptions] = useState<Option[]>([]);
//     const [options, setOptions] = useState<Option[]>([]);
//     const [open, setOpen] = useState(false);
//     const [inputValue, setInputValue] = useState("");
//     const [loading, setLoading] = useState(false);
//     const [testSelectedOption, setTestSelectedOption] = useState<string>("");
//     const [allSelected, setAllSelected] = useState(false);
//     const [page, setPage] = useState(0);
//     const [hasMore, setHasMore] = useState(true);
//     const inputRef = useRef<HTMLInputElement | null>(null);

//     const allOptions = useMemo(() => (
//         fieldType === InputType.MultiSelect
//             ? [{ OptionID: -1, OptionName: "Select All" }, ...options]
//             : options
//     ), [options, fieldType]);

//     const fetchData = useCallback(async (searchKey: string) => {
//         setLoading(true);
//         try {
//             const { data } = await userServicer.getDropDownlist(searchKey, null);
//             const transformed = data.map((item: any) => ({
//                 OptionID: item[Object.keys(item)[0]],
//                 OptionName: item[Object.keys(item)[1]] ?? item[Object.keys(item)[0]],
//             }));
//             setTempAllOptions(transformed);
//             setOptions(transformed.slice(0, limit));
//             setPage(1);
//             setHasMore(transformed.length > limit);
//         } catch (err) {
//             console.error("Dropdown fetch error:", err);
//             setOptions([]);
//         } finally {
//             setLoading(false);
//         }
//     }, []);

//     useEffect(() => {
//         fetchData(autoSearchKey);
//     }, [autoSearchKey, fetchData]);

//     useEffect(() => {
//         if (selectedValues !== testSelectedOption) {
//             setTestSelectedOption(selectedValues);
//         }
//     }, [selectedValues]);

//     useEffect(() => {
//         if (testSelectedOption && tempAllOptions.length > 0) {
//             const ids = testSelectedOption.split(',').map(id => id.trim());
//             const selected = tempAllOptions.filter(
//                 o => ids.includes(o.OptionID.toString()) || ids.includes(o.OptionName)
//             );
//             onChange(name, headerName, selected);
//         }
//     }, [testSelectedOption, tempAllOptions]);

//     const handleInputChange = (_: any, value: string) => {
//         setInputValue(value);
//         if (!value.trim()) {
//             setOptions(tempAllOptions.slice(0, limit));
//             setPage(1);
//             setHasMore(true);
//             return;
//         }
//         const filtered = tempAllOptions.filter((o) =>
//             o.OptionName.toLowerCase().includes(value.toLowerCase())
//         );
//         setOptions(filtered.slice(0, limit));
//         setHasMore(false);
//     };

//     const handleChange = (
//         event: React.SyntheticEvent,
//         newValues: Option[] | Option
//     ) => {
//         event.preventDefault();
//         event.stopPropagation();

//         const normalized: Option[] = Array.isArray(newValues) ? newValues : [newValues];
//         let updated = [...normalized];

//         if (fieldType === InputType.MultiSelect) {
//             if (normalized.some(opt => opt.OptionID === -1)) {
//                 updated = allSelected ? [] : [...tempAllOptions];
//                 setAllSelected(!allSelected);
//             } else {
//                 setAllSelected(updated.length === options.length);
//             }
//         }

//         const idsCSV = updated.map(opt => String(opt.OptionID).trim()).join(",");
//         setTestSelectedOption(idsCSV);
//         onChange(name, headerName, updated);
//     };

//     const handleSingleChange = (_: React.SyntheticEvent, value: Option | null) => {
//         const updated = value ? [value] : [];
//         const idsCSV = updated.map(opt => String(opt.OptionID).trim()).join(",");
//         setTestSelectedOption(idsCSV);
//         onChange(name, headerName, updated);
//     };

//     const handleScroll = (e: React.SyntheticEvent) => {
//         const node = e.currentTarget;
//         if (
//             node.scrollTop + node.clientHeight >= node.scrollHeight - 10 &&
//             hasMore &&
//             !loading
//         ) {
//             const next = tempAllOptions.slice(page * limit, (page + 1) * limit);
//             setOptions((prev) => [...prev, ...next]);
//             setPage((p) => p + 1);
//             if (next.length < limit) setHasMore(false);
//         }
//     };

//     const selectedOptions = useMemo(() => {
//         if (!testSelectedOption || tempAllOptions.length === 0) return [];
//         const ids = testSelectedOption.split(',').map(id => id.trim());
//         const normalized = tempAllOptions.filter(
//             opt => ids.includes(opt.OptionID.toString()) || ids.includes(opt.OptionName)
//         );
//         return normalized;
//     }, [testSelectedOption, tempAllOptions]);

//     return (
//         <>
//             {fieldType === InputType.MultiSelect ? (
//                 <div className={`report-filter ${open ? "dropdown" : ""} `}>
//                     <Autocomplete<Option, true, false, false>
//                         multiple
//                         inputValue={inputValue}
//                         options={allOptions}
//                         limitTags={1}
//                         open={open}
//                         loading={loading}
//                         onOpen={() => setOpen(true)}
//                         onClose={() => setOpen(false)}
//                         disablePortal
//                         disableCloseOnSelect
//                         getOptionLabel={(option) => option.OptionName}
//                         value={selectedOptions}
//                         isOptionEqualToValue={(opt, val) => opt.OptionID === val.OptionID}
//                         getLimitTagsText={() =>
//                             allSelected
//                                 ? `+${tempAllOptions.length - 1}`
//                                 : `+${selectedOptions.length - 1}`
//                         }
//                         ListboxProps={{ onScroll: handleScroll }}
//                         onChange={handleChange}
                        
//                         onInputChange={handleInputChange}
//                         renderOption={(props, option, { selected }) => (
//                             <Box component="li" {...props} key={option.OptionID}>
//                                 <Checkbox checked={option.OptionID === -1 ? allSelected : selected} />
//                                 {option.OptionName}
//                             </Box>
//                         )}
//                         renderInput={(params) => (
//                             <TextField
//                                 {...params}
//                                 label={label}
//                                 variant="standard"
//                                 inputRef={inputRef}
//                                 onClick={() => setOpen(true)}
//                                 InputLabelProps={{
//                                     style: { color: "#243C5C", fontSize: "12px" },
//                                 }}
//                                 InputProps={{
//                                     ...params.InputProps,
//                                     endAdornment: (
//                                         <>
//                                             {loading && (
//                                                 <CircularProgress color="inherit" size={16} />
//                                             )}
//                                             {params.InputProps.endAdornment}
//                                         </>
//                                     ),
//                                 }}
//                             />
//                         )}
//                     />
//                 </div>
//             ) : (
//                 <Autocomplete<Option, false, false, false>
//                     disablePortal
//                     options={allOptions}
//                     value={selectedOptions[0] || null}
//                     onChange={handleSingleChange}
//                     getOptionLabel={(option) => option.OptionName}
//                     isOptionEqualToValue={(opt, val) => opt.OptionID === val.OptionID}
//                     renderOption={(props, option) => (
//                         <Box component="li" {...props} key={option.OptionID}>
//                             {option.OptionName}
//                         </Box>
//                     )}
//                     renderInput={(params) => (
//                         <TextField
//                             {...params}
//                             label={label}
//                             variant="standard"
//                             inputRef={inputRef}
//                             InputLabelProps={{
//                                 style: {
//                                     color: "#243C5C",
//                                     fontSize: "12px",
//                                 },
//                             }}
//                         />
//                     )}
//                 />
//             )}
//         </>
//     );
// };

// export default MultiSelect2;
